/**
 * SecurityPanel — Phase E. Read-mostly dashboard for the auth/security
 * subsystem. Lives under Admin → Maintenance.
 *
 * Cards rendered top → bottom:
 *   1. Session policy           (customer 365d / admin 24h + warnings)
 *   2. Admin MFA roster         (info@, magictuners@, …) with regenerate
 *   3. Google OAuth status      (client id presence + signup flag)
 *   4. SMTP / email health      (config + last 5 deliveries)
 *   5. Login rate-limit buckets (active + locked, clear button per row)
 *   6. Recent admin activity    (live tail, 10s polling)
 *   7. Auth audit log viewer    (last 200, prefix filter)
 *
 * STRICTLY READ-ONLY apart from:
 *   • DELETE /admin/security/rate-limit-bucket  (clear one bucket)
 *   • POST   /auth/mfa/regenerate-backup-codes  (own account only)
 *   • POST   /auth/mfa/admin-reset              (reset another admin MFA)
 * All three are step-up-gated server-side; this UI uses the existing
 * `fetchWithStepUp` wrapper so the StepUpModal pops automatically.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, Loader2, AlertTriangle,
  RefreshCw, Trash2, Activity, CheckCircle2, XCircle, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithStepUp } from '../StepUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  } catch { return String(iso); }
};

const authHeader = (token) => token ? { Authorization: `Bearer ${token}` } : {};

// ───────────────────────────────────────────────────────────────────

function Card({ title, icon, children, right }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center text-slate-700">
            {icon}
          </div>
          <h3 className="font-semibold text-slate-800">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, intent }) {
  const color = intent === 'good' ? 'text-emerald-700'
              : intent === 'bad'  ? 'text-rose-700'
              : intent === 'warn' ? 'text-amber-700'
              : 'text-slate-800';
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-sm font-medium ${color}`}>{value}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────

export default function SecurityPanel({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/security/overview`, { headers: authHeader(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j); setError('');
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-center text-sm text-slate-500" data-testid="security-panel-loading">
        <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading security overview…
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="bg-white border border-rose-200 rounded-xl shadow-sm p-6 text-sm text-rose-700" data-testid="security-panel-error">
        <AlertTriangle className="w-4 h-4 inline mr-1" /> {error || 'No data'}
        <button onClick={fetchOverview} className="ml-3 underline">Retry</button>
      </section>
    );
  }

  return (
    <div className="space-y-4" data-testid="security-panel">
      <SessionPolicyCard policy={data.session_policy} />
      <AdminMfaCard token={token} admins={data.admin_mfa} onRefresh={fetchOverview} />
      <GoogleOAuthCard oauth={data.google_oauth} />
      <SmtpCard smtp={data.smtp} onRefresh={fetchOverview} />
      <RateLimitCard token={token} rl={data.rate_limit} onRefresh={fetchOverview} />
      <RecentActivityCard token={token} />
      <AuditLogCard token={token} />
    </div>
  );
}

// ─── 1. Session policy ─────────────────────────────────────────────

function SessionPolicyCard({ policy }) {
  if (!policy) return null;
  const hasWarn = (policy.warnings || []).length > 0;
  return (
    <Card title="Session policy" icon={<Shield className="w-5 h-5" />}>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <Stat
          label="Customer session max"
          value={`${policy.customer_max_days}d`}
          intent={Math.abs(policy.customer_max_days - policy.expected_customer_max_days) < 0.01 ? 'good' : 'warn'}
        />
        <Stat
          label="Admin session max"
          value={`${policy.admin_max_hours}h`}
          intent={Math.abs(policy.admin_max_hours - policy.expected_admin_max_hours) < 0.01 ? 'good' : 'warn'}
        />
      </div>
      {hasWarn && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 space-y-1" data-testid="session-policy-warning">
          {policy.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
      {!hasWarn && (
        <div className="text-xs text-slate-500 flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          Session caps match expected policy ({policy.expected_customer_max_days}d customer / {policy.expected_admin_max_hours}h admin).
        </div>
      )}
    </Card>
  );
}

// ─── 2. Admin MFA roster ───────────────────────────────────────────

function AdminMfaCard({ token, admins, onRefresh }) {
  const [busy, setBusy] = useState(null);    // email currently being acted on
  const [codesShown, setCodesShown] = useState(null);  // {email, codes[]}

  const regenerateOwn = async () => {
    // Reuses the existing step-up gated endpoint. Server returns the
    // codes ONCE; we show them in a sticky panel for the admin to copy.
    const code = window.prompt('Enter your current 6-digit TOTP and password — separated by a comma:\nFormat:  123456,YourCurrentPassword');
    if (!code) return;
    const [totp, ...rest] = code.split(',');
    const password = rest.join(',').trim();
    if (!totp || !password) {
      toast.error('Please provide both code and password.'); return;
    }
    setBusy('self');
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/auth/mfa/regenerate-backup-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(token) },
        body: JSON.stringify({ code: totp.trim(), password }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setCodesShown({ email: 'self', codes: j.backup_codes || [] });
      toast.success('New backup codes generated. Old codes invalidated.');
      onRefresh();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(null); }
  };

  const resetTargetMfa = async (target) => {
    if (!window.confirm(`Reset MFA for ${target.email}? They will need to enrol again on next login.`)) return;
    setBusy(target.email);
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/auth/mfa/admin-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(token) },
        body: JSON.stringify({ target_user_id: target.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`MFA reset for ${target.email}.`);
      onRefresh();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(null); }
  };

  const setExempt = async (target, nextExempt) => {
    // Toggle MFA exemption. When ON, the target admin logs in without
    // any TOTP challenge; secret/codes are also wiped so the slot is
    // visually clean. Requires superadmin server-side; the existing
    // step-up wrapper re-asks the caller for their own TOTP first.
    const msg = nextExempt
      ? `Disable MFA requirement for ${target.email}?\n\nThis admin will be able to log in with email + password only. Their existing MFA setup and backup codes will be wiped. This setting is per-admin and can be re-enabled at any time.`
      : `Re-enable MFA requirement for ${target.email}?\n\nOn next login the admin will be forced to enrol a fresh authenticator (their previous secret is already cleared).`;
    if (!window.confirm(msg)) return;
    setBusy(target.email);
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/auth/mfa/admin-set-exempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(token) },
        body: JSON.stringify({ target_user_id: target.id, exempt: nextExempt }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(nextExempt
        ? `MFA disabled for ${target.email}.`
        : `MFA requirement restored for ${target.email}.`);
      onRefresh();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(null); }
  };

  return (
    <Card
      title="Admin MFA"
      icon={<ShieldCheck className="w-5 h-5" />}
      right={(
        <button onClick={regenerateOwn} disabled={busy === 'self'} className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="regenerate-own-backup-codes">
          {busy === 'self' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Regenerate my backup codes
        </button>
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Email</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Tier</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">MFA</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Enrolled</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Backup codes</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Active</th>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Required?</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(admins || []).map((a) => (
              <tr key={a.id} className="border-b last:border-0" data-testid={`admin-mfa-row-${a.email}`}>
                <td className="py-2 pr-3 font-medium">{a.email}</td>
                <td className="py-2 pr-3 text-slate-600">{a.admin_tier || '—'}</td>
                <td className="py-2 pr-3">
                  {a.mfa_enabled
                    ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> on</span>
                    : <span className="text-rose-700 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> off</span>}
                </td>
                <td className="py-2 pr-3 text-slate-600">{fmtDt(a.mfa_enrolled_at)}</td>
                <td className="py-2 pr-3 tabular-nums">{a.backup_codes_remaining}</td>
                <td className="py-2 pr-3">
                  {a.is_active
                    ? <span className="text-emerald-700">active</span>
                    : <span className="text-slate-500">disabled{a.admin_disabled_at ? ` · ${fmtDt(a.admin_disabled_at)}` : ''}</span>}
                </td>
                <td className="py-2 pr-3">
                  {/* Required column: shows whether this admin MUST pass MFA on login.
                      Click toggles `mfa_exempt`. Server-side only superadmins may
                      flip it; the rendered toggle is hidden for non-superadmin
                      callers by the API (button still renders, but the click will
                      return 403, which we surface as a toast). */}
                  <button
                    onClick={() => setExempt(a, !a.mfa_exempt)}
                    disabled={busy === a.email || !a.is_active}
                    data-testid={`mfa-exempt-toggle-${a.email}`}
                    title={a.mfa_exempt
                      ? 'MFA bypassed — click to require again'
                      : 'MFA required — click to disable for this admin'}
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border transition ${
                      a.mfa_exempt
                        ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                        : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                    }`}
                  >
                    {a.mfa_exempt ? 'OFF (bypassed)' : 'ON (required)'}
                  </button>
                </td>
                <td className="py-2 pl-2 text-right">
                  {a.is_active && a.mfa_enabled && (
                    <button onClick={() => resetTargetMfa(a)} disabled={busy === a.email} className="text-xs text-rose-700 hover:underline" data-testid={`reset-mfa-${a.email}`}>
                      {busy === a.email ? '…' : 'Reset MFA'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {codesShown && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 space-y-2" data-testid="regenerated-backup-codes">
          <div className="font-semibold">Save these now — they will not be shown again:</div>
          <div className="grid grid-cols-2 gap-1 font-mono select-all">
            {codesShown.codes.map((c, i) => <div key={i}>{c}</div>)}
          </div>
          <button onClick={() => setCodesShown(null)} className="mt-1 underline">I saved them — dismiss</button>
        </div>
      )}
    </Card>
  );
}

// ─── 3. Google OAuth ───────────────────────────────────────────────

function GoogleOAuthCard({ oauth }) {
  if (!oauth) return null;
  return (
    <Card title="Google OAuth" icon={<ShieldCheck className="w-5 h-5" />}>
      <div className="grid grid-cols-2 gap-4">
        <Stat label="Configured" value={oauth.configured ? 'yes' : 'no'} intent={oauth.configured ? 'good' : 'bad'} />
        <Stat label="Client ID (suffix)" value={oauth.client_id_suffix || '—'} />
        <Stat label="Self-signup allowed" value={oauth.allow_self_signup ? 'YES (env)' : 'no'} intent={oauth.allow_self_signup ? 'warn' : 'good'} />
        <Stat label="Primary admin" value={oauth.primary_admin_email} />
        <Stat label="Backup admin" value={oauth.backup_admin_email} />
      </div>
    </Card>
  );
}

// ─── 4. SMTP / email health ────────────────────────────────────────

function SmtpCard({ smtp, onRefresh }) {
  if (!smtp) return null;
  const cfgTs = smtp.updated_at ? new Date(smtp.updated_at).getTime() : 0;
  // Only count failures AFTER the current config was saved as live.
  const liveFailure = (() => {
    if (!smtp.last_delivery_failed) return null;
    const failTs = new Date(smtp.last_delivery_failed).getTime();
    if (cfgTs && failTs < cfgTs) return null;   // stale
    return 'Last delivery attempt failed under the active config. Check Email sender → Test connection.';
  })();
  const colorFor = (kind) => {
    if (kind === 'delivery_ok' || kind === 'connection_test_ok') return 'text-emerald-700';
    if (kind === 'delivery_failed') return 'text-rose-700';
    return 'text-slate-700';
  };
  const labelFor = (kind) => ({
    delivery_ok:         'delivery ok',
    delivery_failed:     'delivery failed',
    connection_test_ok:  'connection test ok',
  })[kind] || kind;
  return (
    <Card
      title="Email / SMTP"
      icon={<Shield className="w-5 h-5" />}
      right={(
        <button onClick={onRefresh} className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="smtp-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      )}
    >
      <div className="grid grid-cols-2 gap-4 mb-3">
        <Stat label="Configured"  value={smtp.configured ? 'yes' : 'no'} intent={smtp.configured ? 'good' : 'bad'} />
        <Stat label="Source"      value={smtp.source || '—'} />
        <Stat label="From"        value={smtp.from_email || '—'} />
        <Stat label="Host:Port"   value={smtp.smtp_host ? `${smtp.smtp_host}:${smtp.smtp_port || '?'}` : '—'} />
        <Stat label="Config updated" value={fmtDt(smtp.updated_at)} />
        <Stat label="Last OK (active config)" value={fmtDt(smtp.last_delivery_ok)} intent={smtp.last_delivery_ok ? 'good' : 'warn'} />
        <Stat label="Last failed (active)"    value={fmtDt(smtp.last_delivery_failed)} intent={smtp.last_delivery_failed ? 'warn' : 'good'} />
        <Stat label="Last test"   value={smtp.last_test_at ? `${fmtDt(smtp.last_test_at)} (${smtp.last_test_ok ? 'OK' : 'FAIL'})` : '—'} />
      </div>
      {liveFailure && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 mb-2 flex items-start gap-1" data-testid="smtp-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {liveFailure}
        </div>
      )}
      {smtp.recent_deliveries?.length > 0 && (
        <details className="text-xs" open>
          <summary className="cursor-pointer text-slate-600">
            Recent activity ({smtp.recent_deliveries.length}) — newest first
          </summary>
          <div className="mt-2 space-y-1 font-mono" data-testid="smtp-recent-list">
            {smtp.recent_deliveries.map((d, i) => {
              const stale = cfgTs && d.ts && new Date(d.ts).getTime() < cfgTs;
              return (
                <div key={i} className={`${colorFor(d.kind)} ${stale ? 'opacity-50' : ''}`} data-testid={`smtp-row-${i}`}>
                  {fmtDt(d.ts)} · {labelFor(d.kind)}{d.recipient ? ` · ${d.recipient}` : ''}{d.error ? ` · ${(d.error || '').slice(0, 100)}` : ''}
                  {stale && <span className="ml-1 text-[10px] uppercase">stale</span>}
                </div>
              );
            })}
          </div>
        </details>
      )}
    </Card>
  );
}

// ─── 5. Rate-limit buckets ─────────────────────────────────────────

function RateLimitCard({ token, rl, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const clear = async (row) => {
    setBusy(`${row.email}|${row.ip}`);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/rate-limit-bucket?email=${encodeURIComponent(row.email)}&ip=${encodeURIComponent(row.ip)}`, {
        method: 'DELETE',
        headers: authHeader(token),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Bucket cleared.');
      onRefresh();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(null); }
  };
  if (!rl) return null;
  return (
    <Card title="Login rate-limit" icon={<ShieldAlert className="w-5 h-5" />}>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <Stat label="Window" value={`${rl.window_seconds / 60}m`} />
        <Stat label="Max attempts" value={rl.max_attempts} />
        <Stat label="Active buckets" value={rl.total_buckets} />
        <Stat label="Locked" value={rl.locked_buckets} intent={rl.locked_buckets > 0 ? 'warn' : 'good'} />
      </div>
      {rl.rows?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-500 border-b">
              <tr>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Email</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">IP</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Attempts</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rl.rows.map((row, i) => (
                <tr key={i} className="border-b last:border-0" data-testid={`rate-limit-row-${i}`}>
                  <td className="py-1 pr-3">{row.email}</td>
                  <td className="py-1 pr-3 font-mono">{row.ip}</td>
                  <td className={`py-1 pr-3 tabular-nums ${row.attempts >= 5 ? 'text-rose-700 font-semibold' : ''}`}>{row.attempts}</td>
                  <td className="py-1 pr-3">{fmtDt(row.expires_at)}</td>
                  <td className="py-1 pl-2 text-right">
                    <button onClick={() => clear(row)} disabled={busy === `${row.email}|${row.ip}`} className="inline-flex items-center gap-1 text-rose-700 hover:underline">
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-slate-500">No active buckets — no recent failed logins.</div>
      )}
    </Card>
  );
}

// ─── 6. Recent admin activity (live tail) ──────────────────────────

function RecentActivityCard({ token }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/security/recent-admin-activity?limit=20`, { headers: authHeader(token) });
      if (r.ok) setRows((await r.json()).rows || []);
    } catch { /* keep last rows on error */ }
    setBusy(false);
  }, [token]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <Card title="Recent admin activity (live)" icon={<Activity className="w-5 h-5" />}>
      {busy && rows.length === 0 ? (
        <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500">No recent activity.</div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-auto text-xs font-mono" data-testid="recent-activity-list">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 py-0.5 border-b last:border-0 border-slate-100">
              <span className="col-span-3 text-slate-500">{fmtDt(r.ts)}</span>
              <span className={`col-span-3 ${r.result === 'fail' || r.result === 'denied' ? 'text-rose-700' : 'text-slate-800'}`}>{r.event}</span>
              <span className="col-span-3 truncate">{r.actor_email || r.target_email || ''}</span>
              <span className="col-span-3 text-slate-500 truncate">{r.ip || ''}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── 7. Audit log viewer ───────────────────────────────────────────

const PREFIX_OPTIONS = [
  { label: 'All',     value: '' },
  { label: 'login.',  value: 'login.' },
  { label: 'mfa.',    value: 'mfa.' },
  { label: 'admin.',  value: 'admin.' },
  { label: 'auth.',   value: 'auth.' },
  { label: 'legacy_password', value: 'legacy_password' },
];

function AuditLogCard({ token }) {
  const [prefix, setPrefix] = useState('');
  const [rows,   setRows]   = useState([]);
  const [busy,   setBusy]   = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const qs = prefix ? `?limit=200&event_prefix=${encodeURIComponent(prefix)}` : '?limit=200';
      const r = await fetch(`${API_URL}/api/admin/security/audit-log${qs}`, { headers: authHeader(token) });
      if (r.ok) setRows((await r.json()).rows || []);
    } catch { setRows([]); }
    setBusy(false);
  }, [token, prefix]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card
      title="Auth audit log"
      icon={<Filter className="w-5 h-5" />}
      right={
        <div className="flex items-center gap-1 flex-wrap">
          {PREFIX_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setPrefix(o.value)}
              className={`text-[11px] px-2 py-0.5 rounded border ${prefix === o.value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`audit-filter-${o.value || 'all'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      }
    >
      {busy && rows.length === 0 ? (
        <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500">No matching audit rows.</div>
      ) : (
        <div className="max-h-[28rem] overflow-auto" data-testid="audit-log-list">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-white border-b text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left py-1 pr-2 whitespace-nowrap">When</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">Event</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">Result</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">Actor</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">Target</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">IP</th>
                <th className="text-left py-1 pr-2 whitespace-nowrap">Meta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1 pr-2 font-mono whitespace-nowrap">{fmtDt(r.ts)}</td>
                  <td className="py-1 pr-2 font-mono">{r.event}</td>
                  <td className={`py-1 pr-2 ${r.result === 'fail' || r.result === 'denied' ? 'text-rose-700' : 'text-emerald-700'}`}>{r.result || ''}</td>
                  <td className="py-1 pr-2 truncate max-w-[180px]">{r.actor_email || ''}</td>
                  <td className="py-1 pr-2 truncate max-w-[180px]">{r.target_email || ''}</td>
                  <td className="py-1 pr-2 font-mono">{r.ip || ''}</td>
                  <td className="py-1 pr-2 text-slate-600 truncate max-w-[280px]">{r.meta ? JSON.stringify(r.meta) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
